import { useState, useEffect } from 'react';

interface KeyboardState {
  isOpen: boolean;
  height: number;
}

export function useKeyboard(): KeyboardState {
  const [state, setState] = useState<KeyboardState>({ isOpen: false, height: 0 });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const onVisualViewport = () => {
      const vv = window.visualViewport;
      if (!vv) return;

      const windowHeight = window.innerHeight;
      const visibleHeight = vv.height;
      const diff = windowHeight - visibleHeight;
      const threshold = 100;

      clearTimeout(timeoutId);
      if (diff > threshold) {
        setState({ isOpen: true, height: diff });
      } else {
        setState({ isOpen: false, height: 0 });
      }
    };

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(onVisualViewport, 100);
    };

    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        setTimeout(() => onVisualViewport(), 50);
      }
    };

    const handleBlur = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => onVisualViewport(), 150);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);
    window.addEventListener('focusin', handleFocus);
    window.addEventListener('focusout', handleBlur);

    // Initial check
    onVisualViewport();

    return () => {
      clearTimeout(timeoutId);
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
      window.removeEventListener('focusin', handleFocus);
      window.removeEventListener('focusout', handleBlur);
    };
  }, []);

  return state;
}

export default useKeyboard;